// Voltage Drop Calculator: field metadata + client-side per-field validation, used for
// instant inline feedback as the user types. The actual math (cable resistance from
// R = rho x L / A, temperature adjustment, per-stage voltage drop, minimum-size
// recommendation) and the authoritative (server-side) validation live in the main
// backend -- see src/lib/toolsApi.js (voltageDropToolApi) and
// backend/tools/voltage_drop_tool.py. Resistivity/temperature-coefficient constants are
// engineering-typical (IEC 60228-style) figures -- see
// backend/core/lookup/voltage_drop_specs.py header for sourcing posture.

export const STANDARD_SIZES_MM2 = [0.35, 0.5, 0.75, 1.0, 1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120, 150, 185, 240];

export const AWG_LABEL_BY_MM2 = {
  0.35: '22 AWG', 0.5: '20 AWG', 0.75: '18 AWG', 1.0: '17 AWG', 1.5: '16 AWG', 2.5: '14 AWG',
  4: '12 AWG', 6: '10 AWG', 10: '8 AWG', 16: '6 AWG', 25: '3 AWG', 35: '2 AWG',
  50: '1/0 AWG', 70: '2/0 AWG', 95: '3/0 AWG', 120: '250 kcmil', 150: '300 kcmil',
  185: '350 kcmil', 240: '500 kcmil',
};

export const VOLTAGE_PRESETS = [12, 24, 48];

export const CONDUCTOR_MATERIALS = [
  { value: 'copper', label: 'Copper' },
  { value: 'aluminum', label: 'Aluminium' },
];

export const LENGTH_MODES = [
  { value: 'one_way', label: 'One-Way' },
  { value: 'total_circuit', label: 'Total Circuit' },
];

export const APPLICATION_PRESETS = [
  {
    value: 'ecu_bms', label: 'ECU / BMS Supply',
    values: {
      supplyVoltageV: 12, loadCurrentA: 2, cableLengthM: 3, lengthMode: 'one_way',
      sizeMm2: 1.5, conductorMaterial: 'copper', cableTempC: 40,
      connectorResistanceMohm: 5, fuseRelayResistanceMohm: 3, groundResistanceMohm: 5,
      maxVoltageDropPercent: 3,
    },
  },
  {
    value: 'sensor_actuator', label: 'Sensor / Actuator',
    values: {
      supplyVoltageV: 12, loadCurrentA: 0.5, cableLengthM: 2, lengthMode: 'one_way',
      sizeMm2: 0.5, conductorMaterial: 'copper', cableTempC: 50,
      connectorResistanceMohm: 8, fuseRelayResistanceMohm: 0, groundResistanceMohm: 5,
      maxVoltageDropPercent: 5,
    },
  },
  {
    value: 'cooling_fan', label: 'Cooling Fan',
    values: {
      supplyVoltageV: 12, loadCurrentA: 15, cableLengthM: 4, lengthMode: 'one_way',
      sizeMm2: 2.5, conductorMaterial: 'copper', cableTempC: 60,
      connectorResistanceMohm: 3, fuseRelayResistanceMohm: 2, groundResistanceMohm: 3,
      maxVoltageDropPercent: 3,
    },
  },
  {
    value: 'pump', label: 'Pump (Coolant / Fuel)',
    values: {
      supplyVoltageV: 12, loadCurrentA: 10, cableLengthM: 5, lengthMode: 'one_way',
      sizeMm2: 2.5, conductorMaterial: 'copper', cableTempC: 45,
      connectorResistanceMohm: 3, fuseRelayResistanceMohm: 2, groundResistanceMohm: 3,
      maxVoltageDropPercent: 3,
    },
  },
  {
    value: 'lighting', label: 'Lighting Circuit',
    values: {
      supplyVoltageV: 12, loadCurrentA: 5, cableLengthM: 6, lengthMode: 'one_way',
      sizeMm2: 1.5, conductorMaterial: 'copper', cableTempC: 35,
      connectorResistanceMohm: 4, fuseRelayResistanceMohm: 2, groundResistanceMohm: 4,
      maxVoltageDropPercent: 3,
    },
  },
  {
    value: 'ev_48v_dcdc', label: '48V DC-DC / Mild-Hybrid Feed',
    values: {
      supplyVoltageV: 48, loadCurrentA: 40, cableLengthM: 2, lengthMode: 'one_way',
      sizeMm2: 10, conductorMaterial: 'copper', cableTempC: 55,
      connectorResistanceMohm: 2, fuseRelayResistanceMohm: 1, groundResistanceMohm: 2,
      maxVoltageDropPercent: 3,
    },
  },
];

// Canonical "Example" button scenario -- distinct from the Application quick-fill
// presets above, used as the guide's worked example too (see static_guides.py).
export const EXAMPLE_INPUTS = {
  supplyVoltageV: 12, loadCurrentA: 20, cableLengthM: 5, lengthMode: 'one_way',
  sizeMm2: 4, conductorMaterial: 'copper', cableTempC: 25,
  connectorResistanceMohm: 5, fuseRelayResistanceMohm: 3, groundResistanceMohm: 4,
  maxVoltageDropPercent: 3,
};

export const DEFAULT_INPUTS = {
  supplyVoltageV: 12,
  loadCurrentA: 10,
  cableLengthM: 3,
  lengthMode: 'one_way',
  sizeMm2: 2.5,
  conductorMaterial: 'copper',
  cableTempC: 25,
  connectorResistanceMohm: 0,
  fuseRelayResistanceMohm: 0,
  groundResistanceMohm: 0,
  maxVoltageDropPercent: 3,
};

// min/max mirror NUMERIC_FIELD_SPECS in backend/core/lookup/voltage_drop_specs.py.
// sizeMm2 is deliberately not here -- it's a dropdown of STANDARD_SIZES_MM2, not a
// free-entry numeric field.
export const NUMERIC_FIELD_SPECS = [
  { key: 'supplyVoltageV', label: 'Supply Voltage', unit: 'V', min: 1, max: 1000, strictlyPositive: true },
  { key: 'loadCurrentA', label: 'Load Current', unit: 'A', min: 0, max: 2000, strictlyPositive: true },
  { key: 'cableLengthM', label: 'Cable Length', unit: 'm', min: 0, max: 500, strictlyPositive: true },
  { key: 'cableTempC', label: 'Cable Temperature', unit: '°C', min: -40, max: 200, strictlyPositive: false },
  { key: 'connectorResistanceMohm', label: 'Connector Resistance', unit: 'mΩ', min: 0, max: 1000, strictlyPositive: false },
  { key: 'fuseRelayResistanceMohm', label: 'Fuse/Relay Resistance', unit: 'mΩ', min: 0, max: 1000, strictlyPositive: false },
  { key: 'groundResistanceMohm', label: 'Ground Resistance', unit: 'mΩ', min: 0, max: 1000, strictlyPositive: false },
  { key: 'maxVoltageDropPercent', label: 'Max Allowable Voltage Drop', unit: '%', min: 0.1, max: 20, strictlyPositive: true },
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}

export const CELSIUS_TO_FAHRENHEIT = (c) => (c * 9) / 5 + 32;
export const FAHRENHEIT_TO_CELSIUS = (f) => ((f - 32) * 5) / 9;
export const M_TO_FT = 3.28084;

export const sizeLabel = (mm2, sizeDisplay) => {
  if (sizeDisplay === 'awg') {
    const awg = AWG_LABEL_BY_MM2[mm2];
    return awg ? `${awg} (${mm2} mm²)` : `${mm2} mm²`;
  }
  return `${mm2} mm²`;
};
